/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { cva, VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import React, { ButtonHTMLAttributes, cloneElement, isValidElement, ReactElement } from 'react'

import { cn } from '@/utils/css'

/** Variants for the button component. */
const buttonVariants = cva(
  'flex items-center justify-center max-w-full rounded-sm font-medium cursor-pointer transition-all duration-200 ease-in-out',
  {
    variants: {
      variant: {
        primary: 'bg-primary-500 text-white hover:bg-primary-600',
        secondary: 'bg-secondary-500 text-white hover:bg-secondary-600',
        grey: 'bg-gray-400 text-white hover:bg-gray-500',
        outlined: 'bg-transparent ring-2 ring-inset hover:bg-opacity-10',
        'outlined-white': 'bg-transparent ring-2 ring-white text-white hover:bg-opacity-10',
        ghost: 'bg-transparent hover:bg-opacity-10',
        'white-primary': 'bg-white text-primary-500 hover:bg-primary-50',
      },
      size: {
        sm: 'px-2 py-1 text-xs min-h-8',
        md: 'px-3 py-2 text-sm min-h-10',
        lg: 'px-4 py-2 text-base min-h-12',
        xl: 'px-6 py-3 text-lg min-h-14',
      },
      fullWidth: {
        true: 'w-full',
        false: 'w-auto',
      },
      uppercase: {
        true: 'uppercase',
        false: 'normal-case',
      },
      rounded: {
        none: 'rounded-none',
        sm: 'rounded-xs',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
        '2xl': 'rounded-2xl',
        '3xl': 'rounded-3xl',
        full: 'rounded-full',
      },
      disabled: {
        true: 'opacity-50 cursor-not-allowed',
        false: '',
      },
      hasIcon: {
        true: 'items-center',
        false: '',
      },
      iconPosition: {
        left: 'flex-row',
        right: 'flex-row-reverse',
      },
    },
    compoundVariants: [
      {
        variant: 'outlined',
        class: 'ring-current',
      },
      {
        variant: ['outlined', 'ghost'],
        class: 'text-current',
      },
      {
        hasIcon: true,
        size: 'sm',
        class: 'gap-1',
      },
      {
        hasIcon: true,
        size: ['md', 'lg', 'xl'],
        class: 'gap-2',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
      uppercase: false,
      rounded: 'md',
      disabled: false,
      hasIcon: false,
      iconPosition: 'left',
    },
  },
)

/** Get the size of the icon based on the size of the button. */
const getIconSize = (size: VariantProps<typeof buttonVariants>['size'], isIconOnly: boolean): number => {
  if (isIconOnly) {
    switch (size) {
      case 'sm':
        return 16
      case 'md':
        return 20
      case 'lg':
        return 24
      case 'xl':
        return 28
      default:
        return 20
    }
  }

  switch (size) {
    case 'sm':
      return 14
    case 'md':
      return 18
    case 'lg':
      return 20
    case 'xl':
      return 24
    default:
      return 18
  }
}

/** A description of available properties for this component. */
type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    icon?: React.ReactNode
    loading?: boolean
  }

/**
 * A highly customisable button component with a set of predefined variants and properties.
 */
const Button: React.FC<ButtonProps> = ({
  variant,
  size,
  fullWidth,
  uppercase,
  rounded,
  disabled,
  className,
  children,
  icon,
  loading,
  iconPosition = 'left',
  ...props
}) => {
  const hasIcon = !!icon || loading
  const isIconOnly = !children
  const iconSize = getIconSize(size, isIconOnly)

  const renderIcon = () => {
    if (loading) {
      return <Loader2 className={'animate-spin'} size={iconSize} />
    }

    if (!icon) return null

    if (isValidElement(icon)) {
      const iconElement = icon as ReactElement<any>
      if ('size' in iconElement.props) {
        return cloneElement(iconElement, { size: iconSize })
      }

      return cloneElement(iconElement, {
        style: { width: `${iconSize}px`, height: `${iconSize}px` },
        width: iconSize,
        height: iconSize,
      })
    }

    return icon
  }

  return (
    <button
      className={cn(
        buttonVariants({
          variant,
          size,
          fullWidth,
          uppercase,
          rounded,
          disabled,
          hasIcon,
          iconPosition,
          className,
        }),
        !children && [
          'aspect-square',
          size === 'sm' && 'w-8',
          size === 'md' && 'w-10',
          size === 'lg' && 'w-12',
          size === 'xl' && 'w-14',
          'inline-flex items-center justify-center',
        ],
      )}
      disabled={disabled || loading}
      {...props}
    >
      {renderIcon()}
      {children}
    </button>
  )
}

export { Button, type ButtonProps }
